import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
    BUDGET_OPTIONS,
    CAPABILITY_OPTIONS,
    TIMELINE_OPTIONS,
} from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
    email: "",
    name: "",
    company: "",
    what_building: "",
    capabilities: [],
    goals: "",
    budget_range: "",
    timeline: "",
};

export const StartProjectModal = ({ open, onOpenChange }) => {
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});

    const setField = (key, value) => {
        setForm((f) => ({ ...f, [key]: value }));
        if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
    };
    const toggleCap = (cap) => {
        setForm((f) => ({
            ...f,
            capabilities: f.capabilities.includes(cap)
                ? f.capabilities.filter((c) => c !== cap)
                : [...f.capabilities, cap],
        }));
    };

    const validate = () => {
        const e = {};
        if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email))
            e.email = "Please enter a valid email.";
        if (!form.what_building || form.what_building.trim().length < 4)
            e.what_building = "Tell us a little more about what you're building.";
        if (!form.goals || form.goals.trim().length < 4)
            e.goals = "Share at least one project goal.";
        if (!form.timeline) e.timeline = "Choose a timeline.";
        if (!form.capabilities.length)
            e.capabilities = "Pick at least one capability.";
        return e;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        const e = validate();
        if (Object.keys(e).length) {
            setErrors(e);
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                email: form.email.trim(),
                name: form.name.trim() || null,
                company: form.company.trim() || null,
                what_building: form.what_building.trim(),
                capabilities: form.capabilities,
                goals: form.goals.trim(),
                budget_range: form.budget_range || null,
                timeline: form.timeline,
                referrer: typeof window !== "undefined" ? window.location.pathname : null,
            };
            await axios.post(`${API}/leads`, payload);
            setSubmitted(true);
            toast.success("Thanks — we'll be in touch within one business day.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string"
                    ? detail
                    : "Something went wrong. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setForm(initialForm);
        setErrors({});
        setSubmitted(false);
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                onOpenChange(v);
                if (!v) {
                    setTimeout(reset, 200);
                }
            }}
        >
            <DialogContent
                className="max-w-3xl p-0 overflow-hidden bg-white border border-black/10 shadow-deep"
                data-testid="start-project-modal"
            >
                <div className="grid grid-cols-1 md:grid-cols-5">
                    <aside className="hidden md:flex md:col-span-2 flex-col justify-between p-7 bg-gradient-to-br from-[hsl(var(--bg-2))] via-[hsl(var(--bg))] to-[hsl(var(--bg-2))] border-r border-black/[0.08] relative overflow-hidden">
                        <div className="absolute -top-12 -right-12 h-64 w-64 rounded-full bg-[rgba(37,99,235,0.10)] blur-3xl pointer-events-none" />
                        <div className="absolute -bottom-16 -left-12 h-72 w-72 rounded-full bg-[rgba(187,118,33,0.10)] blur-3xl pointer-events-none" />
                        <div className="relative z-10">
                            <div className="inline-flex items-center gap-2 rounded-full ring-1 ring-black/10 bg-white/80 backdrop-blur px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-mono text-[hsl(var(--text))]">
                                <Sparkles className="h-3 w-3 text-[hsl(var(--gold))]" />
                                Start a Project
                            </div>
                            <DialogTitle className="mt-6 font-display text-3xl leading-tight text-[hsl(var(--text))]">
                                Tell us what you’re trying to build — we’ll come back with a point of view.
                            </DialogTitle>
                            <DialogDescription className="mt-3 text-[13.5px] leading-relaxed text-[hsl(var(--text-muted))]">
                                We respond within one business day with a brief, a senior partner, and — if it’s a fit — a small team shaped to your outcome.
                            </DialogDescription>
                        </div>
                        <ul className="relative z-10 mt-8 space-y-3 text-[13px] text-[hsl(var(--text-muted))]">
                            <li className="flex gap-2.5 items-start">
                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[hsl(var(--gold))]" />
                                Curated specialists, not a marketplace.
                            </li>
                            <li className="flex gap-2.5 items-start">
                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[hsl(var(--blue))]" />
                                AI agents accelerate, humans elevate.
                            </li>
                            <li className="flex gap-2.5 items-start">
                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[hsl(var(--gold))]" />
                                You own the systems we leave behind.
                            </li>
                        </ul>
                    </aside>

                    <div className="md:col-span-3 p-6 md:p-7 max-h-[85vh] overflow-y-auto">
                        {submitted ? (
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                                className="flex flex-col items-start gap-4 py-12"
                                data-testid="start-project-success"
                            >
                                <div className="h-12 w-12 rounded-full bg-[rgba(187,118,33,0.10)] ring-1 ring-[rgba(187,118,33,0.35)] inline-flex items-center justify-center">
                                    <CheckCircle2 className="h-6 w-6 text-[hsl(var(--gold))]" />
                                </div>
                                <h3 className="font-display text-2xl text-[hsl(var(--text))]">
                                    Thanks — we’ll be in touch within one business day.
                                </h3>
                                <p className="text-sm text-[hsl(var(--text-muted))] leading-relaxed">
                                    A senior partner will read your brief personally, then come back with an initial point of view, references, and the right people for the work.
                                </p>
                                <button
                                    onClick={() => onOpenChange(false)}
                                    className="mt-2 inline-flex items-center gap-2 rounded-full bg-[hsl(var(--bg-2))] hover:bg-[hsl(var(--bg-3))] ring-1 ring-black/10 text-[hsl(var(--text))] px-4 py-2 text-sm transition-colors"
                                >
                                    Close
                                </button>
                            </motion.div>
                        ) : (
                            <form
                                onSubmit={submit}
                                className="flex flex-col gap-5"
                                data-testid="start-project-intake-form"
                                noValidate
                            >
                                <DialogTitle className="sr-only">
                                    Start a Project intake form
                                </DialogTitle>
                                <Field
                                    label="What are you trying to build?"
                                    error={errors.what_building}
                                >
                                    <textarea
                                        rows={3}
                                        value={form.what_building}
                                        onChange={(e) =>
                                            setField("what_building", e.target.value)
                                        }
                                        placeholder="e.g. A demand engine for our enterprise pipeline; a new brand launch; an internal AI assistant…"
                                        className="w-full rounded-xl bg-[hsl(var(--bg-2))] ring-1 ring-inset ring-black/10 focus-visible:ring-2 focus-visible:ring-[hsl(var(--blue))] text-[14.5px] text-[hsl(var(--text))] placeholder:text-[hsl(var(--text-subtle))] px-4 py-3 outline-none transition-shadow"
                                        data-testid="intake-what-building"
                                    />
                                </Field>

                                <Field
                                    label="Capabilities you need"
                                    error={errors.capabilities}
                                    hint="Multi-select"
                                >
                                    <div
                                        className="flex flex-wrap gap-2"
                                        data-testid="intake-capabilities"
                                    >
                                        {CAPABILITY_OPTIONS.map((cap) => {
                                            const active = form.capabilities.includes(cap);
                                            return (
                                                <button
                                                    key={cap}
                                                    type="button"
                                                    data-testid={`intake-capability-${cap.toLowerCase().replace(/\s/g,'-')}`}
                                                    onClick={() => toggleCap(cap)}
                                                    className={`rounded-full px-3.5 py-1.5 text-[12.5px] tracking-tight ring-1 ring-inset transition-colors ${
                                                        active
                                                            ? "bg-[rgba(187,118,33,0.10)] ring-[rgba(187,118,33,0.45)] text-[hsl(var(--text))]"
                                                            : "bg-[hsl(var(--bg-2))] ring-black/10 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"
                                                    }`}
                                                    aria-pressed={active}
                                                >
                                                    {cap}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </Field>

                                <Field label="Project goals" error={errors.goals}>
                                    <textarea
                                        rows={3}
                                        value={form.goals}
                                        onChange={(e) => setField("goals", e.target.value)}
                                        placeholder="What outcome would make this a success in 90 days?"
                                        className="w-full rounded-xl bg-[hsl(var(--bg-2))] ring-1 ring-inset ring-black/10 focus-visible:ring-2 focus-visible:ring-[hsl(var(--blue))] text-[14.5px] text-[hsl(var(--text))] placeholder:text-[hsl(var(--text-subtle))] px-4 py-3 outline-none"
                                        data-testid="intake-goals"
                                    />
                                </Field>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="Budget range" hint="Optional">
                                        <select
                                            value={form.budget_range}
                                            onChange={(e) => setField("budget_range", e.target.value)}
                                            className="w-full rounded-xl bg-[hsl(var(--bg-2))] ring-1 ring-inset ring-black/10 focus-visible:ring-2 focus-visible:ring-[hsl(var(--blue))] text-[14.5px] text-[hsl(var(--text))] px-4 py-3 outline-none"
                                            data-testid="intake-budget"
                                        >
                                            <option value="">Select range…</option>
                                            {BUDGET_OPTIONS.map((b) => (
                                                <option key={b} value={b}>
                                                    {b}
                                                </option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label="Timeline" error={errors.timeline}>
                                        <select
                                            value={form.timeline}
                                            onChange={(e) => setField("timeline", e.target.value)}
                                            className="w-full rounded-xl bg-[hsl(var(--bg-2))] ring-1 ring-inset ring-black/10 focus-visible:ring-2 focus-visible:ring-[hsl(var(--blue))] text-[14.5px] text-[hsl(var(--text))] px-4 py-3 outline-none"
                                            data-testid="intake-timeline"
                                        >
                                            <option value="">Choose…</option>
                                            {TIMELINE_OPTIONS.map((t) => (
                                                <option key={t} value={t}>
                                                    {t}
                                                </option>
                                            ))}
                                        </select>
                                    </Field>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="Your name" hint="Optional">
                                        <input
                                            type="text"
                                            value={form.name}
                                            onChange={(e) => setField("name", e.target.value)}
                                            placeholder="Jane Doe"
                                            className="w-full rounded-xl bg-[hsl(var(--bg-2))] ring-1 ring-inset ring-black/10 focus-visible:ring-2 focus-visible:ring-[hsl(var(--blue))] text-[14.5px] text-[hsl(var(--text))] placeholder:text-[hsl(var(--text-subtle))] px-4 py-3 outline-none"
                                            data-testid="intake-name"
                                        />
                                    </Field>
                                    <Field label="Company" hint="Optional">
                                        <input
                                            type="text"
                                            value={form.company}
                                            onChange={(e) => setField("company", e.target.value)}
                                            placeholder="Northwind"
                                            className="w-full rounded-xl bg-[hsl(var(--bg-2))] ring-1 ring-inset ring-black/10 focus-visible:ring-2 focus-visible:ring-[hsl(var(--blue))] text-[14.5px] text-[hsl(var(--text))] placeholder:text-[hsl(var(--text-subtle))] px-4 py-3 outline-none"
                                            data-testid="intake-company"
                                        />
                                    </Field>
                                </div>

                                <Field label="Email" error={errors.email}>
                                    <input
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => setField("email", e.target.value)}
                                        placeholder="jane@company.com"
                                        className="w-full rounded-xl bg-[hsl(var(--bg-2))] ring-1 ring-inset ring-black/10 focus-visible:ring-2 focus-visible:ring-[hsl(var(--blue))] text-[14.5px] text-[hsl(var(--text))] placeholder:text-[hsl(var(--text-subtle))] px-4 py-3 outline-none"
                                        data-testid="intake-email"
                                    />
                                </Field>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="start-project-submit-button"
                                    className="sheen group mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[hsl(var(--text))] hover:bg-[hsl(var(--text))]/90 text-[hsl(var(--bg))] px-5 py-3 text-[14px] font-semibold disabled:opacity-70 transition-colors"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Sending…
                                        </>
                                    ) : (
                                        <>
                                            Send brief
                                            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                                        </>
                                    )}
                                </button>
                                <p className="text-[11.5px] text-[hsl(var(--text-subtle))] leading-relaxed">
                                    By submitting you agree to be contacted by IdeaClap. We never share your information.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

const Field = ({ label, hint, error, children }) => (
    <label className="flex flex-col gap-2">
        <span className="flex items-center justify-between">
            <span className="text-[12px] uppercase tracking-[0.16em] font-mono text-[hsl(var(--text-muted))]">
                {label}
            </span>
            {hint && (
                <span className="text-[11px] text-[hsl(var(--text-subtle))]">{hint}</span>
            )}
        </span>
        {children}
        {error && (
            <span className="text-[12px] text-[hsl(var(--destructive))]">
                {error}
            </span>
        )}
    </label>
);

export default StartProjectModal;
